// ============================================================================
// Hooks TanStack Query — ADMINISTRATION (AD-C) : onglet Membres
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (P-ADM-2, routes/admin.ts : canAccessGestAdmin = admin SEUL — fonctions repo
// ADMIN-WIDE, jamais le grant par cible). Le bundle members est charge par
// onglet (P-ADM-8) sous ADMIN_ROOT_KEY. Les mutations invalident la famille
// admin ET les surfaces qui lisent les profils (org, profil, equipes, gestion) —
// parite PoC revalidatePath('/admin' + '/organigramme' + '/equipes' + profil).
// STOP-5 : aucune mutation de changement de courriel ici (rekey non porte).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { ADMIN_ROOT_KEY, type GestAdminRole } from './useAdminAudit';

// ─── Types — MIROIR EXACT des DTOs serveur (routes/admin.ts AD-C) ─────────────

/** Role Systeme C brut (ug.UserCompanyBcRoles) — null = aucun role GEST. */
export type SystemCRole = 'UTILISATEUR' | 'GESTIONNAIRE' | 'ADMIN';

/** Ligne membre (miroir AdminMemberDto). Le role est AFFICHE, jamais editable (STOP-4a). */
export interface AdminMember {
  email: string;
  fullName: string | null;
  matricule: string | null;
  teamId: string | null;
  committee: string | null;
  managerEmail: string | null;
  /** Statut d'emploi METIER (P-ADM-18) — pas le soft-delete is_active. */
  isEmployed: boolean;
  systemCRole: SystemCRole | null;
  /** false = fiche pre-provisionnee, compte lie au 1er login Entra (P-ADM-6). */
  hasUserAccount: boolean;
}

/** Equipe du bundle (miroir OrgTeamDto — noms + selects + derivation director). */
export interface AdminMemberTeam {
  id: string;
  name: string;
  directorEmail: string | null;
}

/** Demande d'inactivation pending (miroir AdminInactivationRequestDto). */
export interface AdminInactivationRequest {
  id: string;
  targetEmail: string;
  targetName: string | null;
  requestedAt: string;
  requestedByName: string | null;
  lastActiveDay: string;
  reason: string;
}

export interface AdminMembersResponse {
  viewer: { role: GestAdminRole; email: string };
  members: AdminMember[];
  teams: AdminMemberTeam[];
  pending: AdminInactivationRequest[];
  /** email -> date de depart ISO (fiche Compensation) ou null. */
  departureDates: Record<string, string | null>;
}

export interface MemberCreatePayload {
  email: string;
  fullName: string | null;
  matricule: string;
  hireDate: string;
  teamId: string | null;
  committee: 'ce' | 'cg' | null;
}

export interface MemberUpdatePayload {
  fullName: string | null;
  matricule: string | null;
  teamId: string | null;
  committee: 'ce' | 'cg' | null;
  managerEmail: string | null;
}

// ─── Role effectif AFFICHE (badge) ────────────────────────────────────────────

/**
 * Miroir du role metier du parc (effectiveCompensationRole serveur) : ADMIN
 * prime ; la qualite de directeur DERIVE de gest.Teams.director_email (STOP-8,
 * jamais un champ de profil) ; sinon GESTIONNAIRE -> manager, reste -> employee.
 */
export function effectiveMemberRole(
  member: AdminMember,
  directorEmails: ReadonlySet<string>,
): GestAdminRole {
  if (member.systemCRole === 'ADMIN') return 'admin';
  if (directorEmails.has(member.email.toLowerCase().trim())) return 'director';
  if (member.systemCRole === 'GESTIONNAIRE') return 'manager';
  return 'employee';
}

// ─── Codes d'erreur metier (GA-21 — pattern managementErrorCode) ──────────────

/**
 * Extrait le code metier VERBATIM renvoye par le serveur ({ code }), sinon un
 * code generique par statut. Mappe sur gest:admin.members.errors.<code>.
 * Codes serveur connus : member_exists, member_notfound, departure_required,
 * manager_self, manager_cycle, manager_not_found, forbidden, notfound, invalid.
 */
export function adminMemberErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string' && code.length > 0 && !code.startsWith('GEST_')) return code;
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

// ─── Query (bundle par onglet, P-ADM-8) ───────────────────────────────────────

const adminMembersKey = [...ADMIN_ROOT_KEY, 'members'] as const;

/** AD-C : bundle de l'onglet MEMBRES (1 requete SPA — liste + file + dates). */
export function useAdminMembers(enabled: boolean) {
  return useQuery({
    queryKey: adminMembersKey,
    queryFn: () => api.get<AdminMembersResponse>('/api/admin/members'),
    enabled,
    retry: false,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

function useInvalidateMembers() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY });
    // Les fiches alimentent l'organigramme, les profils, les equipes et
    // l'onglet Gestion (parite PoC revalidatePath multi-surfaces).
    void qc.invalidateQueries({ queryKey: ['gest', 'org'] });
    void qc.invalidateQueries({ queryKey: ['gest', 'profile'] });
    void qc.invalidateQueries({ queryKey: ['gest', 'teams'] });
    void qc.invalidateQueries({ queryKey: ['gest', 'management'] });
  };
}

/** AD-C (STOP-4a) : creation d'une FICHE RH — jamais de compte ug ni de role. */
export function useCreateMember() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (payload: MemberCreatePayload) =>
      api.post<{ ok: boolean }>('/api/admin/members', payload),
    onSuccess: () => invalidate(),
  });
}

/** AD-C : rattachement (nom/matricule/equipe/comite/responsable — PAS le role). */
export function useUpdateMember() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (input: { email: string; payload: MemberUpdatePayload }) =>
      api.put<{ ok: boolean }>(
        `/api/admin/members/${encodeURIComponent(input.email)}`,
        input.payload,
      ),
    onSuccess: () => invalidate(),
  });
}

/** AD-C (P-ADM-18) : statut d'emploi — date de depart obligatoire a la desactivation. */
export function useSetMemberEmployed() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (input: { email: string; employed: boolean; departureDate: string | null }) =>
      api.post<{ ok: boolean }>(
        `/api/admin/members/${encodeURIComponent(input.email)}/employed`,
        { employed: input.employed, departureDate: input.departureDate ?? '' },
      ),
    onSuccess: () => invalidate(),
  });
}

/** AD-C (P-ADM-17) : decision d'une demande d'inactivation (approve/reject). */
export function useDecideInactivation() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (input: { id: string; decision: 'approved' | 'rejected'; decisionNote: string }) =>
      api.post<{ ok: boolean; alreadyDecided: boolean }>(
        `/api/admin/inactivations/${encodeURIComponent(input.id)}/decide`,
        { decision: input.decision, decisionNote: input.decisionNote },
      ),
    onSuccess: () => invalidate(),
  });
}
